import { Button, Card, CardContent, CardHeader, CardTitle } from '@folio/components';
import { useAssertedParams } from '@folio/lib';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  policyControllerGetPolicyQueryOptions,
  retryUntilValue,
  viewSpecificControllerListCustomerInvoices,
} from 'korint-sdk';
import { CheckCircleIcon, Loader2Icon, XCircleIcon } from 'lucide-react';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

function FirstPaymentStatus() {
  const { t } = useTranslation();
  const { policyId, branchId } = useAssertedParams();

  const { data: customerId } = useSuspenseQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId }),
    select: data => data.customers?.[0],
  });

  const { data: { hasPaid, hasFailed } = {} } = useSuspenseQuery({
    queryKey: ['customer-invoices', customerId],
    queryFn: async () => {
      async function getFirstPaymentStatus() {
        const data = await viewSpecificControllerListCustomerInvoices(customerId!);

        return {
          hasPaid: data.data.some(invoice => invoice.status === 'PAID'),
          hasFailed: data.data.some(invoice => invoice.status === 'PAYMENT_FAILED'),
        };
      }

      return await retryUntilValue(getFirstPaymentStatus, {
        delayMs: 1000,
        useExponentialBackoff: true,
        maxAttempts: 5,
        isValidResult: ({ hasFailed, hasPaid }) => hasPaid || hasFailed,
      });
    },
  });

  if (hasPaid) {
    return (
      <>
        <CheckCircleIcon className="h-10 w-10 text-green-600" />
        <div className="text-sm text-muted-foreground">
          {t('policyholderConfirmation.paymentSucceeded')}
        </div>
        <Link to={`/policyholder/${policyId}`}>
          <Button>{t('policyholderConfirmation.goToMySpace')}</Button>
        </Link>
      </>
    );
  }

  if (hasFailed) {
    return (
      <>
        <XCircleIcon className="h-10 w-10 text-red-600" />
        <div className="text-sm text-muted-foreground">
          {t('policyholderConfirmation.paymentFailed')}
        </div>
        <Link to="../payment-method" relative="path">
          <Button>{t('policyholderConfirmation.chooseAnotherPaymentMethod')}</Button>
        </Link>
      </>
    );
  }

  return (
    <div className="text-sm text-muted-foreground">
      {t('policyholderConfirmation.paymentError')}
    </div>
  );
}

export function PolicyholderConfirmationStep() {
  const { t } = useTranslation();
  const { policyId, branchId } = useAssertedParams();

  const { data: shouldPay } = useSuspenseQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId }),
    select: data => data.invoicingConfig.firstPayment !== 'NONE',
  });

  if (!shouldPay) {
    return (
      <Card>
        <CardHeader className="border-b">
          <CardTitle>{t('policyholderConfirmation.policySigned')}</CardTitle>
        </CardHeader>
        <CardContent className="py-4 h-96 w-full flex flex-col items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <CheckCircleIcon className="h-10 w-10 text-green-600" />
            <Link to={`/policyholder/${policyId}`}>
              <Button>{t('policyholderConfirmation.goToMySpace')}</Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="border-b">
        <CardTitle>{t('policyholderConfirmation.firstPaymentTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="py-4 h-96 w-full flex flex-col items-center justify-center">
        <Suspense
          fallback={
            <div className="top-0 left-0 bottom-0 right-0 py-32 flex items-center justify-center">
              <Loader2Icon className="w-8 h-8 animate-spin" />
              <div className="text-sm text-muted-foreground">
                {t('policyholderConfirmation.paymentPending')}
              </div>
            </div>
          }
        >
          <FirstPaymentStatus />
        </Suspense>
      </CardContent>
    </Card>
  );
}
